/**
 * Support contact configuration — the ONE place the driver app reads
 * its support address from.
 *
 * Every support contact action ("Email support", "Chat with us", the
 * FAQ page's "Still need help?") builds its mailto: from here, so a
 * change of address is a build-time env change, never a code edit.
 *
 * Env-var contract (build time, like every VITE_* value):
 *   - `VITE_SUPPORT_EMAIL` — optional. Unset, blank, or not a plain
 *     email address → `support@greencharge.app`.
 */

export const DEFAULT_SUPPORT_EMAIL = 'support@greencharge.app';

/** Subject line "Chat with us" pre-fills, so support can tell the two apart. */
export const SUPPORT_CHAT_SUBJECT = 'GreenCharge chat';

/** In-app FAQ route ("Help centre" on the Support page). */
export const FAQ_ROUTE = '/support/faq';

// Deliberately strict: a plain local@domain.tld with no query/header
// characters, so a mis-set env value can never smuggle `?bcc=` or a
// scheme into the mailto: link.
const PLAIN_EMAIL = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

/**
 * Resolve the configured support address, falling back to the default.
 * Pure so tests can call it with synthetic values.
 */
export function resolveSupportEmail(raw) {
  const value = typeof raw === 'string' ? raw.trim() : '';
  return PLAIN_EMAIL.test(value) ? value : DEFAULT_SUPPORT_EMAIL;
}

/** Build a mailto: link, optionally with a subject. */
export function buildSupportMailto(email, subject) {
  const base = `mailto:${email}`;
  return subject ? `${base}?subject=${encodeURIComponent(subject)}` : base;
}

const RAW_SUPPORT_EMAIL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPPORT_EMAIL) || '';

export const SUPPORT_EMAIL = resolveSupportEmail(RAW_SUPPORT_EMAIL);
export const SUPPORT_MAILTO = buildSupportMailto(SUPPORT_EMAIL);
export const SUPPORT_CHAT_MAILTO = buildSupportMailto(SUPPORT_EMAIL, SUPPORT_CHAT_SUBJECT);
